import React, { useEffect, useState } from "react";
import {
  FaSearch,
  FaFilter,
  FaDownload,
  FaEye,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PAGE_SIZE = 10;

type CertificateRow = {
  attemptId: number;
  studentId: number;
  firstName: string;
  lastName: string;
  studentEmail: string;
  level: string;
  examSetName: string;
  batchName: string;
  submittedAt: string;
  totalScore: number;
  maxScore: number;
  percentage: number;
  passed: boolean;
};

const AdminCertificates: React.FC = () => {
  const [rows, setRows] = useState<CertificateRow[]>([]);
  const [filtered, setFiltered] = useState<CertificateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [batchFilter, setBatchFilter] = useState("ALL");

  // Pagination
  const [page, setPage] = useState(1);

  // Per-row loading state
  const [pdfLoading, setPdfLoading] = useState<Record<number, "view" | "download" | null>>({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
    setPage(1);
  }, [rows, search, levelFilter, statusFilter, batchFilter]);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/exams/results/all`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load");
      const data: CertificateRow[] = await res.json();
      setRows(data);
    } catch {
      setError("Failed to load certificates.");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let data = [...rows];
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.firstName.toLowerCase().includes(q) ||
          r.lastName.toLowerCase().includes(q) ||
          r.studentEmail.toLowerCase().includes(q) ||
          r.examSetName.toLowerCase().includes(q) ||
          r.batchName.toLowerCase().includes(q)
      );
    }
    if (levelFilter !== "ALL") data = data.filter((r) => r.level === levelFilter);
    if (statusFilter === "PASSED") data = data.filter((r) => r.passed);
    if (statusFilter === "FAILED") data = data.filter((r) => !r.passed);
    if (batchFilter !== "ALL") data = data.filter((r) => r.batchName === batchFilter);
    setFiltered(data);
  };

  const uniqueBatches = rows
    .map((r) => r.batchName)
    .filter((b, i, arr) => arr.indexOf(b) === i)
    .sort();

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const fetchPdf = async (attemptId: number): Promise<{ blob: Blob; filename: string }> => {
    const res = await fetch(
      `${API_BASE_URL}/api/admin/exams/attempts/${attemptId}/result/pdf`,
      { credentials: "include" }
    );
    if (!res.ok) throw new Error("Failed");
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `attempt-${attemptId}.pdf`;
    const blob = await res.blob();
    return { blob, filename };
  };

  const handleView = async (attemptId: number) => {
    setPdfLoading((p) => ({ ...p, [attemptId]: "view" }));
    try {
      const { blob } = await fetchPdf(attemptId);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      alert("Failed to load PDF.");
    } finally {
      setPdfLoading((p) => ({ ...p, [attemptId]: null }));
    }
  };

  const handleDownload = async (attemptId: number) => {
    setPdfLoading((p) => ({ ...p, [attemptId]: "download" }));
    try {
      const { blob, filename } = await fetchPdf(attemptId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Failed to download PDF.");
    } finally {
      setPdfLoading((p) => ({ ...p, [attemptId]: null }));
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
          <input
            type="text"
            placeholder="Name, Email, Exam, Batch..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#009fe3]"
          />
        </div>

        <div className="flex items-center gap-2">
          <FaFilter size={13} className="text-gray-400" />
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#009fe3]"
          >
            <option value="ALL">All Levels</option>
            <option value="A1">A1</option>
            <option value="A2">A2</option>
            <option value="B1">B1</option>
            <option value="B2">B2</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#009fe3]"
          >
            <option value="ALL">All Status</option>
            <option value="PASSED">Passed</option>
            <option value="FAILED">Failed</option>
          </select>

          <select
            value={batchFilter}
            onChange={(e) => setBatchFilter(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#009fe3]"
          >
            <option value="ALL">All Batches</option>
            {uniqueBatches.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>

        <span className="text-sm text-gray-500 ml-auto">
          {filtered.length} certificate{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#009fe3]">
            <FaSpinner className="animate-spin mr-2" />
            <span>Loading...</span>
          </div>
        ) : error ? (
          <div className="py-10 text-center text-red-500">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-gray-400">No published certificates found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                <tr>
                  {["Student", "Level", "Exam", "Batch", "Submitted", "Score", "Status", "PDF"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginated.map((row, i) => {
                  const busy = pdfLoading[row.attemptId];
                  return (
                    <tr
                      key={row.attemptId}
                      className={`${i % 2 === 1 ? "bg-gray-50" : "bg-white"} hover:bg-blue-50 transition-colors`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900 text-sm">
                          {row.firstName} {row.lastName}
                        </div>
                        <div className="text-xs text-gray-400">{row.studentEmail}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-[#009fe3] text-white">
                          {row.level}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.examSetName}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.batchName}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(row.submittedAt)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                        {row.totalScore}/{row.maxScore}
                        <span className="block text-xs text-gray-500 font-normal">
                          {row.percentage?.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {row.passed ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            <FaCheckCircle size={10} /> Passed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-1 rounded-full">
                            <FaTimesCircle size={10} /> Failed
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleView(row.attemptId)}
                            disabled={!!busy}
                            className="p-1.5 bg-[#009fe3] text-white rounded hover:bg-cyan-600 disabled:opacity-50 transition-colors"
                            title="View PDF"
                          >
                            {busy === "view" ? <FaSpinner className="animate-spin" size={13} /> : <FaEye size={13} />}
                          </button>
                          <button
                            onClick={() => handleDownload(row.attemptId)}
                            disabled={!!busy}
                            className="p-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                            title="Download PDF"
                          >
                            {busy === "download" ? <FaSpinner className="animate-spin" size={13} /> : <FaDownload size={13} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl shadow px-4 py-3">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages} — showing {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              <FaChevronLeft size={12} />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const pageNum = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page + i - 3;
              if (pageNum < 1 || pageNum > totalPages) return null;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 rounded text-sm font-semibold transition-colors ${
                    pageNum === page
                      ? "bg-[#009fe3] text-white"
                      : "border border-gray-200 hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              <FaChevronRight size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCertificates;
